import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

const shortExamples = ['materials', 'bathroom', 'kitchen', 'painting'] as const;
const completePlan = ['groundwork', 'foundation', 'frame', 'roof', 'facade', 'finishes', 'handover'] as const;

export default function MilestoneGuidance() {
  const { t } = useTranslation();
  const id = useId();
  const [expanded, setExpanded] = useState<'short' | 'complete' | null>(null);

  return (
    <section className="milestone-guidance mb-3" aria-labelledby={`${id}-title`}>
      <div className="alert alert-info mb-2">
        <h6 id={`${id}-title`} className="fw-semibold">
          <i className="bi bi-info-circle me-2" aria-hidden="true" />
          {t('milestones.guidance.title')}
        </h6>
        <p className="small mb-0">{t('milestones.guidance.description')}</p>
      </div>
      <div className="accordion">
        {(['short', 'complete'] as const).map(section => (
          <div className="accordion-item" key={section}>
            <h6 className="accordion-header">
              <button
                type="button"
                id={`${id}-${section}-toggle`}
                className={`accordion-button small${expanded === section ? '' : ' collapsed'}`}
                aria-expanded={expanded === section}
                aria-controls={`${id}-${section}`}
                onClick={() => setExpanded(current => current === section ? null : section)}
              >
                {t(`milestones.guidance.${section}.toggle`)}
              </button>
            </h6>
            <div
              id={`${id}-${section}`}
              role="region"
              aria-labelledby={`${id}-${section}-toggle`}
              hidden={expanded !== section}
            >
              <div className="accordion-body p-3 small">
                {section === 'short' ? (
                  <ul className="list-unstyled mb-0">
                    {shortExamples.map(example => (
                      <li key={example} className="mb-3">
                        <h6 className="fw-semibold">{t(`milestones.guidance.short.${example}.title`)}</h6>
                        <p className="text-muted mb-1">{t('milestones.guidance.short.vague', { description: t(`milestones.guidance.short.${example}.vague`) })}</p>
                        <p className="mb-0">{t(`milestones.guidance.short.${example}.description`)}</p>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <>
                    <h6 className="fw-semibold">{t('milestones.guidance.complete.title')}</h6>
                    <p>{t('milestones.guidance.complete.scope')}</p>
                    <ol className="list-group list-group-numbered mb-3">
                      {completePlan.map(step => (
                        <li key={step} className="list-group-item d-flex align-items-start gap-2 p-3">
                          <div className="text-break">
                            <h6 className="fw-semibold mb-1">{t(`milestones.guidance.complete.${step}.title`)}</h6>
                            <p className="mb-0">{t(`milestones.guidance.complete.${step}.description`)}</p>
                          </div>
                        </li>
                      ))}
                    </ol>
                    <p>{t('milestones.guidance.complete.connection')}</p>
                  </>
                )}
                <p className="text-muted mb-0">{t('milestones.guidance.adapt')}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
